/* eslint-disable react/jsx-props-no-spreading -- the three views take the same grid props, and the day panel takes the post handlers */
import React, { useMemo } from 'react';
import clsx from 'clsx';
import { TypedMemo } from '../../../common/types';
import { AgendaList } from './AgendaList';
import type { CalendarGridProps } from './calendarGridTypes';
import { DayPanel } from './DayPanel';
import type { PostPanelHandlers } from './dayPanelTypes';
import { MonthGrid } from './MonthGrid';
import { PostSheet } from './PostSheet';
import type { CalendarScreenState } from './useCalendarScreen';
import { WeekGrid } from './WeekGrid';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for the Calendar; each part uses its own slice of it */
import styles from './PromoteCalendar.module.scss';
/* eslint-enable css-modules/no-unused-class */

interface CalendarBodyProps {
  screen: CalendarScreenState;
  panelHandlers: PostPanelHandlers;
}

/**
 * The view of the Calendar and the day panel next to it (design b1). From 700 px to below 1200 px
 * (Bootstrap xl) the panel is a drawer over the grid (CSS only), and below 700 px it is a bottom
 * sheet (design c2). In both cases the grid keeps the full width, so its cards fit.
 */
const CalendarBodyComponent: React.FC<CalendarBodyProps> = ({ screen, panelHandlers }) => {
  const { range, contentByDay, songs, move, selectedPost, panelDay, panelContent, filters, channels } = screen;
  const xPosts = useMemo(() => channels.find((channel) => channel.platform === 'x')?.xPosts ?? null, [channels]);
  const grid: CalendarGridProps = {
    days: range.days,
    contentByDay,
    songs,
    drag: move.drag,
    selectedUuid: selectedPost?.uuid ?? null,
    selectedDayKey: panelDay?.key ?? null,
    onOpenPost: screen.onOpenPost,
    onOpenDay: screen.onOpenDay,
    slotsFor: screen.slotsFor,
    onAdd: screen.onAdd,
  };
  const panel =
    panelDay === null ? null : (
      <DayPanel
        day={panelDay}
        content={panelContent}
        songs={songs}
        selectedPost={selectedPost}
        slots={screen.slotsFor(panelDay.start, panelContent.posts)}
        onSelect={screen.onOpenPost}
        onClose={screen.closePanel}
        onAdd={screen.onAdd}
        xPosts={xPosts}
        {...panelHandlers}
      />
    );

  return (
    <div className={clsx(styles.split, panelDay !== null && !screen.isNarrow && styles.splitOpen)}>
      <div className={styles.splitMain}>
        {filters.view === 'month' ? <MonthGrid {...grid} /> : null}
        {filters.view === 'week' ? <WeekGrid {...grid} /> : null}
        {filters.view === 'list' ? <AgendaList {...grid} /> : null}
      </div>
      {screen.isNarrow ? (
        <PostSheet isOpen={panelDay !== null} onClose={screen.closePanel}>
          {panel}
        </PostSheet>
      ) : (
        panel
      )}
      <div className="visually-hidden" aria-live="assertive" data-testid="calendar-live">
        {move.drag.liveText}
      </div>
    </div>
  );
};

export const CalendarBody = TypedMemo(CalendarBodyComponent);
